import { formatElement, formatAbilityType, elementClass, formatTarget } from '../../components/GameTerms';
import { buildSkillAbilityLines } from '../../components/TooltipContent';
import { getAbilityIconUrl } from '../../components/ItemIcon';
import { getConfig, escapeHtml } from './types';

export interface SkillsState {
  tagFilter: string;
  elementFilter: string;
  typeFilter: string;
  masteryFilter: string;
  searchTerm: string;
  showMoreFilters: boolean;
  expandedSkillId: string | null;
}

export function createSkillsState(): SkillsState {
  return { tagFilter: '', elementFilter: '', typeFilter: '', masteryFilter: '', searchTerm: '', showMoreFilters: false, expandedSkillId: null };
}

export function renderSkills(content: HTMLElement, state: SkillsState): void {
  if (state.expandedSkillId) {
    renderSkillDetail(content, state);
    return;
  }

  const config = getConfig();
  if (!config || !config.skillDefs) { content.innerHTML = '<p>Loading...</p>'; return; }

  // Hide internal skills (minion companion abilities like bone_slash) — players see them via the summon tooltip
  const allSkills = Object.values(config.skillDefs).filter(s => !s.internal);

  // Collect filter options from skill abilities
  const tagSet = new Set<string>();
  const elementSet = new Set<string>();
  const typeSet = new Set<string>();
  for (const s of allSkills) {
    const ability = config.abilities[s.abilityId];
    if (!ability) continue;
    if (ability.element) elementSet.add(ability.element);
    if (ability.type) typeSet.add(ability.type);
    for (const tag of (ability.tags ?? [])) {
      if (tag !== 'player' && tag !== 'enemy') tagSet.add(tag);
    }
  }

  const masteryOptions = Object.values(config.masteries)
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(m => `<option value="${m.id}"${state.masteryFilter === m.id ? ' selected' : ''}>${m.name}</option>`)
    .join('');

  const elementPills = [...elementSet].sort().map(el =>
    `<button class="codex-pill ${elementClass(el)}${state.elementFilter === el ? ' active' : ''}" data-skill-element="${el}">${formatElement(el)}</button>`
  ).join('');

  const typePills = [...typeSet].sort().map(t =>
    `<button class="codex-pill${state.typeFilter === t ? ' active' : ''}" data-skill-type="${t}">${formatAbilityType(t)}</button>`
  ).join('');

  const tagPills = [...tagSet].sort()
    .filter(t => !elementSet.has(t))
    .filter(t => !typeSet.has(t))
    .map(t => `<button class="codex-pill${state.tagFilter === t ? ' active' : ''}" data-skill-tag="${t}">${formatElement(t)}</button>`)
    .join('');

  const moreOpen = state.showMoreFilters;
  const hasMoreActive = state.typeFilter || state.tagFilter;

  content.innerHTML = `
    <h2 class="codex-section-title">Skills</h2>
    <p class="codex-section-subtitle"></p>
    <div class="codex-skill-filters-row">
      <input class="codex-filter-input" type="text" placeholder="Search skills..." value="${escapeHtml(state.searchTerm)}" />
      <select class="codex-filter-select codex-skill-mastery-select">
        <option value="">All Masteries</option>
        ${masteryOptions}
      </select>
    </div>
    <div class="codex-filter-pills">
      ${elementPills}
    </div>
    <button class="codex-disclosure-btn${moreOpen ? ' open' : ''}${hasMoreActive ? ' has-active' : ''}" data-more-toggle>
      <span class="codex-disclosure-caret">${moreOpen ? '▼' : '▶'}</span>
      More filters${hasMoreActive ? ' <span class="codex-disclosure-dot"></span>' : ''}
    </button>
    <div class="codex-disclosure-body${moreOpen ? '' : ' hidden'}">
      ${typePills ? `<div class="codex-filter-pills-label">Type</div><div class="codex-filter-pills">${typePills}</div>` : ''}
      ${tagPills ? `<div class="codex-filter-pills-label">Tag</div><div class="codex-filter-pills">${tagPills}</div>` : ''}
    </div>
    <div class="codex-skill-grid"></div>
  `;

  content.querySelector('.codex-filter-input')?.addEventListener('input', (e) => {
    state.searchTerm = (e.target as HTMLInputElement).value;
    renderSkillResults(content, state);
  });

  content.querySelector('.codex-skill-mastery-select')?.addEventListener('change', (e) => {
    state.masteryFilter = (e.target as HTMLSelectElement).value;
    renderSkillResults(content, state);
  });

  content.querySelectorAll('[data-skill-element]').forEach(btn => {
    btn.addEventListener('click', () => {
      const el = (btn as HTMLElement).dataset.skillElement!;
      state.elementFilter = state.elementFilter === el ? '' : el;
      renderSkills(content, state);
    });
  });

  content.querySelectorAll('[data-skill-type]').forEach(btn => {
    btn.addEventListener('click', () => {
      const t = (btn as HTMLElement).dataset.skillType!;
      state.typeFilter = state.typeFilter === t ? '' : t;
      renderSkills(content, state);
    });
  });

  content.querySelectorAll('[data-skill-tag]').forEach(btn => {
    btn.addEventListener('click', () => {
      const t = (btn as HTMLElement).dataset.skillTag!;
      state.tagFilter = state.tagFilter === t ? '' : t;
      renderSkills(content, state);
    });
  });

  content.querySelector('[data-more-toggle]')?.addEventListener('click', () => {
    state.showMoreFilters = !state.showMoreFilters;
    renderSkills(content, state);
  });

  renderSkillResults(content, state);
}

function renderSkillResults(content: HTMLElement, state: SkillsState): void {
  const config = getConfig();
  if (!config || !config.skillDefs) return;

  const allSkills = Object.values(config.skillDefs).filter(s => !s.internal);

  let filtered = allSkills;
  if (state.searchTerm) {
    const term = state.searchTerm.toLowerCase();
    const stripHtml = (s: string) => s.replace(/<[^>]*>/g, ' ');
    filtered = filtered.filter(s => {
      const ability = config.abilities[s.abilityId];
      if (s.name.toLowerCase().includes(term) ||
          s.abilityId.toLowerCase().includes(term)) return true;
      if (ability) {
        const topRank = s.ranks?.[s.ranks.length - 1];
        const lines = buildSkillAbilityLines(ability, topRank, config);
        const tooltipText = stripHtml(lines.join(' ')).toLowerCase();
        if (tooltipText.includes(term)) return true;
      }
      return false;
    });
  }
  if (state.masteryFilter) {
    filtered = filtered.filter(s => {
      const ability = config.abilities[s.abilityId];
      if (ability?.masteryId === state.masteryFilter) return true;
      // Also include skills whose rank1 mastery requirement matches
      const rank1 = s.ranks?.[0];
      const reqs = rank1?.masteryRequirements ?? [];
      return reqs.some(r => r.masteryId === state.masteryFilter);
    });
  }
  if (state.elementFilter) {
    filtered = filtered.filter(s => {
      const ability = config.abilities[s.abilityId];
      return ability?.element === state.elementFilter;
    });
  }
  if (state.typeFilter) {
    filtered = filtered.filter(s => {
      const ability = config.abilities[s.abilityId];
      return ability?.type === state.typeFilter;
    });
  }
  if (state.tagFilter) {
    filtered = filtered.filter(s => {
      const ability = config.abilities[s.abilityId];
      return (ability?.tags ?? []).includes(state.tagFilter);
    });
  }

  /** Pick the mastery group key for a skill: prefer ability.masteryId, else first rank requirement. */
  const masteryKey = (s: typeof allSkills[number]): string => {
    const ability = config.abilities[s.abilityId];
    if (ability?.masteryId) return ability.masteryId;
    const reqs = s.ranks?.[0]?.masteryRequirements ?? [];
    if (reqs.length > 0) return reqs[0].masteryId;
    return '';
  };

  // Group by mastery, sorted alphabetically by mastery name. Within a mastery, alpha sort.
  const groups = new Map<string, typeof filtered>();
  for (const s of filtered) {
    const key = masteryKey(s);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(s);
  }
  const sortedGroups = [...groups.entries()].sort((a, b) => {
    // "Other" (empty key) sinks to the bottom
    if (!a[0] && b[0]) return 1;
    if (a[0] && !b[0]) return -1;
    const an = config.masteries?.[a[0]]?.name ?? '';
    const bn = config.masteries?.[b[0]]?.name ?? '';
    return an.localeCompare(bn);
  });
  for (const [, list] of sortedGroups) {
    list.sort((a, b) => a.name.localeCompare(b.name));
  }

  const renderCard = (s: typeof allSkills[number]): string => {
    const ability = config.abilities[s.abilityId];
    if (!ability) return '';

    const elCls = elementClass(ability.element);
    const rank1 = s.ranks?.[0];
    const topRank = s.ranks?.[s.ranks.length - 1];

    // Rank-1 unlock requirement gets its own meta line so players can scan
    // unlock thresholds without opening each skill. Suppress the duplicate body line.
    const rank1Reqs = rank1?.masteryRequirements ?? [];
    const unlockHtml = rank1Reqs.length > 0
      ? `<div class="codex-skill-meta"><span class="codex-skill-unlock">${rank1Reqs.map(r => {
          const m = config.masteries?.[r.masteryId];
          return `${m?.name ?? r.masteryId} R${r.rank}`;
        }).join(' + ')}</span></div>`
      : '';
    const rankBadge = `<span class="codex-skill-rank">${s.ranks.length} rank${s.ranks.length > 1 ? 's' : ''}</span>`;

    // Body shows the top-rank numbers so players see the skill's full potential.
    // Unlock rank is still surfaced separately above (rank1Reqs meta line).
    const abilityLines = buildSkillAbilityLines(ability, topRank, config, { hideRequirements: true });
    const abilityHtml = abilityLines.join('');

    const cardIconSrc = getAbilityIconUrl(s.abilityId);

    return `
      <div class="codex-skill-card" data-skill-id="${s.id}">
        <div class="codex-skill-header">
          <img class="codex-skill-card-icon" src="${cardIconSrc}" alt="" width="24" height="24" draggable="false" onerror="this.style.display='none'">
          <div class="codex-skill-header-text">
            <div class="codex-skill-name-row">
              <span class="codex-skill-name ${elCls}">${s.name}</span>
              ${rankBadge}
            </div>
            ${unlockHtml}
          </div>
        </div>
        <div class="codex-skill-card-body">${abilityHtml}</div>
      </div>
    `;
  };

  const groupHtml = sortedGroups.map(([key, list]) => {
    const label = key ? (config.masteries?.[key]?.name ?? key) : 'Other';
    return `
      <div class="codex-family-group">
        <div class="codex-family-header">${label} <span class="codex-family-count">${list.length}</span></div>
        <div class="codex-skill-grid">${list.map(renderCard).join('')}</div>
      </div>
    `;
  }).join('');

  const subtitle = content.querySelector('.codex-section-subtitle');
  if (subtitle) subtitle.textContent = `${filtered.length} / ${allSkills.length}`;

  // Re-render path first: if the groups container already exists from a prior render, just
  // swap its innerHTML. Must check this BEFORE looking for `.codex-skill-grid`, because each
  // group renders an inner `.codex-skill-grid` for its cards — querying for it on re-render
  // would match the first group's sub-grid and nest the new groups inside, accumulating
  // duplicate mastery headers per keystroke.
  const groupsContainer = content.querySelector('.codex-skills-groups');
  if (groupsContainer) {
    groupsContainer.innerHTML = groupHtml || '<p class="codex-empty">No skills match filters</p>';
  } else {
    // First render: replace the placeholder grid added by renderSkills().
    const grid = content.querySelector('.codex-skill-grid');
    if (grid) {
      grid.outerHTML = `<div class="codex-skills-groups">${groupHtml || '<p class="codex-empty">No skills match filters</p>'}</div>`;
    }
  }

  content.querySelectorAll('[data-skill-id]').forEach(card => {
    card.addEventListener('click', () => {
      state.expandedSkillId = (card as HTMLElement).dataset.skillId!;
      renderSkills(content, state);
      content.scrollTop = 0;
    });
  });
}

function renderSkillDetail(content: HTMLElement, state: SkillsState): void {
  const config = getConfig();
  if (!config || !config.skillDefs || !state.expandedSkillId) { state.expandedSkillId = null; renderSkills(content, state); return; }

  const s = config.skillDefs[state.expandedSkillId];
  if (!s) { state.expandedSkillId = null; renderSkills(content, state); return; }

  const ability = config.abilities[s.abilityId];
  if (!ability) { state.expandedSkillId = null; renderSkills(content, state); return; }

  const elCls = `element-${ability.element}`;
  const mastery = ability.masteryId ? config.masteries?.[ability.masteryId] : null;
  const iconSrc = getAbilityIconUrl(s.abilityId);
  const targetStr = ability.type === 'summon' ? '' : formatTarget(ability.target);

  const topRank = s.ranks?.[s.ranks.length - 1];
  const topRankLines = buildSkillAbilityLines(ability, topRank, config, { rankCount: s.ranks.length });

  const rankCardsHtml = s.ranks.map((r, i) => {
    const lines = buildSkillAbilityLines(ability, r, config, { hideRequirements: true });
    const reqs = r.masteryRequirements ?? [];
    const gateHtml = reqs.length > 0
      ? `<span class="gd-rank-card-gate">${reqs.map(req => {
          const m = config.masteries?.[req.masteryId];
          return `${m?.name ?? req.masteryId} R${req.rank}`;
        }).join(', ')}</span>`
      : '';
    return `<div class="gd-rank-card">
      <div class="gd-rank-card-header">
        <span class="gd-rank-badge">${i + 1}</span>
        <span class="gd-rank-card-title">Rank ${i + 1}</span>
        ${gateHtml}
      </div>
      <div class="gd-rank-card-body">${lines.join('')}</div>
    </div>`;
  }).join('');

  content.innerHTML = `
    <button class="codex-back-btn" id="codex-skill-back">Back</button>
    <div class="codex-detail-page">
      <div class="gd-hero">
        <img class="gd-icon" src="${iconSrc}" alt="" width="56" height="56" draggable="false" onerror="this.style.display='none'">
        <div class="gd-hero-info">
          <div class="codex-detail-name ${elCls}">${s.name}</div>
          <div class="gd-meta">
            <span class="codex-pill ${elCls}">${formatElement(ability.element)}</span>
            ${mastery ? `<span class="gd-mastery">${mastery.name}</span>` : ''}
            ${targetStr ? `<span class="gd-target">${targetStr}</span>` : ''}
          </div>
        </div>
      </div>

      <div class="codex-detail-card gd-description">
        ${topRankLines.join('')}
      </div>

      <div class="gd-ranks-section">
        <div class="gd-section-label">All Ranks</div>
        <div class="gd-rank-list">${rankCardsHtml}</div>
      </div>
    </div>
  `;

  content.querySelector('#codex-skill-back')?.addEventListener('click', () => {
    state.expandedSkillId = null;
    renderSkills(content, state);
    content.scrollTop = 0;
  });
}
